import { useRef, useLayoutEffect } from "react";
import type { ReactNode } from "react";

import { Typography } from "@aviary/components/Typography";
import { useOverlayContext } from "@shared/context/OverlayContext";

import * as styles from "./ModalHeader.styles";

interface Props {
  /**
   * Optionally add a drop shadow to the bottom the header
   *
   * @default false
   */
  hasShadow?: boolean;
  /**
   * Optionally add a tagline to be added beneath the passed in title
   *
   */
  tagLine?: string;
  /**
   * Content to render within the modal header
   *
   * @default undefined
   */
  children?: ReactNode;
}

const ModalHeader = ({ children, tagLine, hasShadow = false, ...rest }: Props) => {
  const getStyles = [styles.ModalHeader, hasShadow && styles.modalShadow];
  const overlayContext = useOverlayContext();
  const headerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!overlayContext || !headerRef.current) return;

    overlayContext.registerTitleElement(headerRef.current);

    return () => overlayContext.registerTitleElement(null);
  }, [overlayContext]);

  return (
    <div css={getStyles} ref={headerRef} {...rest}>
      <div css={styles.content}>
        {children}
        {!!tagLine && <Typography>{tagLine}</Typography>}
      </div>
    </div>
  );
};

export { ModalHeader };
